import { JWMHero } from "@components/JWMHero/JWMHero";
import analyticstracker from "@jmc/analyticstracker";
import { CarouselSlideTracking } from "@jmc/core/src/components/CarouselSlideTracking/index";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { ImageCarousel } from "@jmc/solid-design-system/src/components/organisms/ImageCarousel/ImageCarousel";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSCarouselProps, CMSCarouselSlide } from "@jwm/types/CMSCarouselProps";
import { CMSColor } from "@jwm/types/CMSColor";
import { default as cmsMapper } from "@utils/cms-mapper";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import { SplitImage } from "./slides";
import style from "./style.module.scss";

interface PropTypes {
    carouselContent: CMSCarouselProps;
    placement: string;
    titleComponent?: React.ElementType;
    loading?: "eager" | "lazy";
    siteSpecialty?: {
        type: string;
        title: string;
    };
    headerVariant?: boolean;
}

export const JWMCarousel = (props: PropTypes): JSX.Element => {
    const {
        carouselContent,
        placement,
        titleComponent,
        loading = "lazy",
        siteSpecialty,
        headerVariant = false,
    } = props;
    const slides = carouselContent?.slides;
    const interval = carouselContent?.interval;

    const carouselProps = cmsMapper.mapCarouselProps(carouselContent);
    const fullWidth = carouselContent?.full_width;

    const { jnjFullBranded } = useJnjBranding();
    const { t } = useTranslation();

    const carouselAriaLabelProps = {
        carouselPreviousButtonAriaLabel: t("Previous", { ns: "common" }),
        carouselNextButtonAriaLabel: t("Next", { ns: "common" }),
        carouselPlayButtonAriaLabel: t("Play", { ns: "common" }),
        carouselPauseButtonAriaLabel: t("Pause", { ns: "common" }),
        carouselSliderAriaLabel: t("Relevant items", { ns: "common" }),
        carouselSlideAriaLabel: t("Slide {{CURRENT_SLIDE_INDEX}} of {{TOTAL_SLIDES}}", {
            CURRENT_SLIDE_INDEX: 0,
            TOTAL_SLIDES: slides?.length,
            ns: "common",
        }),
        carouselSlideDotAriaLabel: t("Move to slide {{CURRENT_SLIDE_INDEX}}", { CURRENT_SLIDE_INDEX: 0, ns: "common" }),
    };

    let carouselHeight: string = null;
    switch (carouselContent?.size) {
        case "Large":
            carouselHeight = "70vh";
            break;
        case "Small":
            carouselHeight = "30vh";
            break;
        case "Medium":
        default:
            carouselHeight = "45vh";
    }

    const getSlideTitle = (slide: CMSCarouselSlide): string => {
        return (
            slide?.title ||
            slide?.image?.description ||
            slide?.image?.title?.substring(0, slide?.image?.title?.lastIndexOf("."))
        );
    };

    // Event tracking on carousel button click
    const eventTracking = (name: string, action: string, index: number): void => {
        const info = {
            name: name,
            placement: placement,
            position: index + 1,
            type: "carousel",
            action: action,
        };
        analyticstracker().trackEvent({
            event: EventTypes.CONTENTBLOCK_CLICK,
            info: info,
        });
    };

    // on image carousel left-arrow, right-arow, carousel-indicator click
    const onActionButtonClick = (action: string, index: number): void => {
        const matchedSlide = slides[index];
        eventTracking(getSlideTitle(matchedSlide), action, index);
    };

    const carouselSlides: JSX.Element[] = [];

    slides?.map((slide, index) => {
        const slideTitle = getSlideTitle(slide);

        if (!jnjFullBranded && slide.display_options.view_mode === "Background") {
            carouselSlides.push(
                <CarouselSlideTracking
                    name={slideTitle}
                    type={"carousel"}
                    placement={placement}
                    position={index}
                    key={`slide-${slideTitle}-${index}`}
                >
                    <JWMHero
                        key={index}
                        {...{
                            ...carouselProps,
                            height: "100%",
                            carouselHeight: carouselHeight,
                            backgroundColor: slide?.image
                                ? "transparent"
                                : cmsMapper.mapCarouselColors(slide?.display_options?.background_color),
                            title: slide?.title,
                            description: slide?.carousel_text,
                            contentAlignment: slide?.display_options?.align_content?.toLowerCase(),
                            contentVerticalAlignment:
                                slide?.display_options?.align_content_vertically?.toLowerCase() as
                                    | "center"
                                    | "top"
                                    | "bottom",
                            id: slide?.title,
                            image: slide?.image?.gatsbyImageData,
                            imagePosition: slide?.display_options?.image_focus as ImagePosition,
                            footer: carouselProps?.footer?.filter((item, footerIndex) => footerIndex === index),
                            hasOverlay: slide?.display_options?.overlay,
                            placement: placement,
                            titleColor: cmsMapper.mapCarouselColors(slide?.display_options?.title_color as CMSColor),
                            textColor: cmsMapper.mapCarouselColors(slide?.display_options?.text_color as CMSColor),
                            linkColor: cmsMapper.mapCarouselColors(
                                slide?.display_options?.sub_title_reference_and_link_color as CMSColor,
                            ),
                        }}
                        showInHeader={true}
                        fullWidth={false}
                        titleComponent={index === 0 ? titleComponent : "span"}
                        loading={index === 0 ? loading : "lazy"}
                        siteSpecialty={siteSpecialty}
                        isCarouselSlider={true}
                    />
                </CarouselSlideTracking>,
            );
        } else {
            carouselSlides.push(
                <CarouselSlideTracking
                    name={slideTitle}
                    type={"carousel"}
                    placement={placement}
                    position={index}
                    key={`slide-${slideTitle}-${index}`}
                >
                    <SplitImage
                        key={index}
                        slide={{
                            ...slide,
                        }}
                        {...carouselProps?.footer?.[index]}
                        showInHeader={true}
                        height={carouselHeight}
                        placement={placement}
                        titleComponent={index === 0 ? titleComponent : "span"}
                        loading={index === 0 ? loading : "lazy"}
                        siteSpecialty={siteSpecialty}
                        headerVariant={headerVariant}
                    />
                </CarouselSlideTracking>,
            );
        }
    });

    return (
        <div
            className={classNames(
                !jnjFullBranded && fullWidth ? style[`carousel__fullWidth`] : style[`carousel__contained`],
                headerVariant && style.headerVariant,
            )}
            data-test-id="carousel"
        >
            <ImageCarousel
                fullWidth={!jnjFullBranded && fullWidth}
                interval={interval}
                actionButtonClick={onActionButtonClick}
                {...carouselAriaLabelProps}
                headerVariant={headerVariant}
            >
                {carouselSlides}
            </ImageCarousel>
        </div>
    );
};
